import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Circle,
  Clock,
  Calendar,
  User,
  Building,
  Target,
  Rocket,
  FileCheck,
  Users,
  Settings,
  PlayCircle,
  PauseCircle,
  AlertCircle,
  CheckSquare,
  Square,
  Plus,
  Edit3,
  Trash2,
  ChevronRight,
  Flag,
  MessageSquare,
  Sparkles,
  Phone,
  Mail,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const API = "/api";

// Default implementation phases/milestones
const DEFAULT_PHASES = [
  { id: "kickoff", name: "Kickoff", description: "Initial project setup and team introductions", order: 1 },
  { id: "discovery", name: "Discovery & Planning", description: "Requirements gathering and project planning", order: 2 },
  { id: "configuration", name: "Configuration", description: "System setup and customization", order: 3 },
  { id: "data_migration", name: "Data Migration", description: "Import existing data and validate", order: 4 },
  { id: "training", name: "Training", description: "User training and documentation", order: 5 },
  { id: "uat", name: "User Acceptance Testing", description: "Final testing and sign-off", order: 6 },
  { id: "go_live", name: "Go-Live", description: "Production deployment and launch", order: 7 },
];

// Default tasks per phase
const DEFAULT_TASKS = {
  kickoff: [
    { name: "Schedule kickoff meeting", priority: "high" },
    { name: "Send welcome package", priority: "medium" },
    { name: "Assign implementation manager", priority: "high" },
    { name: "Create project Slack channel", priority: "low" },
  ],
  discovery: [
    { name: "Conduct requirements workshop", priority: "high" },
    { name: "Document current workflows", priority: "medium" },
    { name: "Identify key stakeholders", priority: "high" },
    { name: "Define success metrics", priority: "medium" },
  ],
  configuration: [
    { name: "Set up user accounts", priority: "high" },
    { name: "Configure integrations", priority: "medium" },
    { name: "Customize workflows", priority: "medium" },
    { name: "Set up reporting dashboards", priority: "low" },
  ],
  data_migration: [
    { name: "Export data from legacy system", priority: "high" },
    { name: "Data cleanup and validation", priority: "high" },
    { name: "Import data to new system", priority: "high" },
    { name: "Verify data integrity", priority: "high" },
  ],
  training: [
    { name: "Create training materials", priority: "medium" },
    { name: "Schedule admin training", priority: "high" },
    { name: "Schedule end-user training", priority: "high" },
    { name: "Record training videos", priority: "low" },
  ],
  uat: [
    { name: "Create test scenarios", priority: "high" },
    { name: "Execute UAT testing", priority: "high" },
    { name: "Document issues found", priority: "medium" },
    { name: "Get sign-off from stakeholders", priority: "high" },
  ],
  go_live: [
    { name: "Final data sync", priority: "high" },
    { name: "Switch DNS/routing", priority: "high" },
    { name: "Monitor system performance", priority: "high" },
    { name: "Send go-live announcement", priority: "medium" },
  ],
};

export default function ImplementationPage() {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [implementation, setImplementation] = useState(null);
  const [activePhase, setActivePhase] = useState(null);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskName, setNewTaskName] = useState("");
  const [newTaskPriority, setNewTaskPriority] = useState("medium");
  
  const token = localStorage.getItem("admin_token");
  const userRole = localStorage.getItem("admin_role") || "admin";

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchLead();
  }, [leadId, token]);

  const fetchLead = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLead(data);
        
        // Initialize or load implementation data
        if (data.implementation) {
          setImplementation(data.implementation);
          // Set active phase to first incomplete phase
          const firstIncomplete = data.implementation.phases?.find(p => p.status !== "completed");
          setActivePhase(firstIncomplete?.id || data.implementation.phases?.[0]?.id);
        } else {
          // Initialize new implementation
          initializeImplementation(data);
        }
      } else {
        toast.error("Lead not found");
        navigate(-1);
      }
    } catch (error) {
      toast.error("Failed to load lead");
    } finally {
      setLoading(false);
    }
  };

  const initializeImplementation = (leadData) => {
    const phases = DEFAULT_PHASES.map(phase => ({
      ...phase,
      status: phase.order === 1 ? "in_progress" : "not_started",
      started_at: phase.order === 1 ? new Date().toISOString() : null,
      completed_at: null,
      tasks: (DEFAULT_TASKS[phase.id] || []).map((task, idx) => ({
        id: `task_${phase.id}_${idx}`,
        ...task,
        completed: false,
        completed_at: null,
        completed_by: null,
      }))
    }));

    const impl = {
      started_at: new Date().toISOString(),
      target_go_live: null,
      implementation_manager: null,
      phases,
      notes: [],
      health: "on_track", // on_track, at_risk, blocked
    };
    
    setImplementation(impl);
    setActivePhase("kickoff");
    
    // Save to backend
    saveImplementation(impl);
  };

  const saveImplementation = async (implData) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/implementation`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ implementation: implData })
      });
      
      if (response.ok) {
        toast.success("Progress saved");
      }
    } catch (error) {
      console.error("Failed to save:", error);
    } finally {
      setSaving(false);
    }
  };

  const toggleTask = async (phaseId, taskId) => {
    const updatedImpl = { ...implementation };
    const phase = updatedImpl.phases.find(p => p.id === phaseId);
    const task = phase?.tasks.find(t => t.id === taskId);
    
    if (task) {
      task.completed = !task.completed;
      task.completed_at = task.completed ? new Date().toISOString() : null;
      task.completed_by = task.completed ? localStorage.getItem("admin_email") : null;
      
      // Check if all tasks in phase are complete
      const allComplete = phase.tasks.every(t => t.completed);
      if (allComplete && phase.status !== "completed") {
        phase.status = "completed";
        phase.completed_at = new Date().toISOString();
        
        // Start next phase
        const nextPhase = updatedImpl.phases.find(p => p.order === phase.order + 1);
        if (nextPhase && nextPhase.status === "not_started") {
          nextPhase.status = "in_progress";
          nextPhase.started_at = new Date().toISOString();
          setActivePhase(nextPhase.id);
        }
        
        toast.success(`${phase.name} phase completed!`);
      }
      
      setImplementation(updatedImpl);
      saveImplementation(updatedImpl);
    }
  };

  const updatePhaseStatus = (phaseId, status) => {
    const updatedImpl = { ...implementation };
    const phase = updatedImpl.phases.find(p => p.id === phaseId);
    
    if (phase) {
      phase.status = status;
      if (status === "in_progress" && !phase.started_at) {
        phase.started_at = new Date().toISOString();
      }
      if (status === "completed") {
        phase.completed_at = new Date().toISOString();
      }
      
      setImplementation(updatedImpl);
      saveImplementation(updatedImpl);
    }
  };

  const addTask = (phaseId) => {
    if (!newTaskName.trim()) return;
    
    const updatedImpl = { ...implementation };
    const phase = updatedImpl.phases.find(p => p.id === phaseId);
    
    if (phase) {
      phase.tasks.push({
        id: `task_${phaseId}_${Date.now()}`,
        name: newTaskName.trim(),
        priority: newTaskPriority,
        completed: false,
        completed_at: null,
        completed_by: null,
      });
      
      setImplementation(updatedImpl);
      saveImplementation(updatedImpl);
      setNewTaskName("");
      setShowAddTask(false);
      toast.success("Task added");
    }
  };

  const updateHealth = (health) => {
    const updatedImpl = { ...implementation, health };
    setImplementation(updatedImpl);
    saveImplementation(updatedImpl);
  };

  const updateTargetDate = (date) => {
    const updatedImpl = { ...implementation, target_go_live: date };
    setImplementation(updatedImpl);
    saveImplementation(updatedImpl);
  };

  const getProgressPercentage = () => {
    if (!implementation?.phases) return 0;
    const totalTasks = implementation.phases.reduce((sum, p) => sum + p.tasks.length, 0);
    const completedTasks = implementation.phases.reduce((sum, p) => sum + p.tasks.filter(t => t.completed).length, 0);
    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  };

  const getPhaseProgress = (phase) => {
    const total = phase.tasks.length;
    const completed = phase.tasks.filter(t => t.completed).length;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not set";
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  const currentPhase = implementation?.phases?.find(p => p.id === activePhase);
  const progress = getProgressPercentage();

  return (
    <div className="min-h-screen bg-[#050505] p-6" data-testid="implementation-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <button
              onClick={() => navigate(`/admin/leads/${leadId}`)}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to {lead?.first_name}'s Profile
            </button>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
                <Rocket className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Implementation Workflow</h1>
                <p className="text-zinc-400">{lead?.company_name || lead?.first_name + " " + lead?.last_name}</p>
              </div>
            </div>
          </div>
          
          {/* Health Status */}
          <div className="flex items-center gap-4">
            <select
              value={implementation?.health || "on_track"}
              onChange={(e) => updateHealth(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2 text-white text-sm"
              data-testid="health-select"
            >
              <option value="on_track">🟢 On Track</option>
              <option value="at_risk">🟡 At Risk</option>
              <option value="blocked">🔴 Blocked</option>
            </select>
            {saving && <Loader2 className="w-4 h-4 animate-spin text-purple-400" />}
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Progress Overview */}
          <div className="col-span-4 space-y-6">
            {/* Overall Progress Card */}
            <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 p-6">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Overall Progress</h3>
              
              <div className="relative w-32 h-32 mx-auto mb-4">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    fill="none"
                    stroke="#27272a"
                    strokeWidth="12"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    fill="none"
                    stroke="url(#progressGradient)"
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={`${progress * 3.52} 352`}
                  />
                  <defs>
                    <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#a855f7" />
                      <stop offset="100%" stopColor="#ec4899" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-3xl font-bold text-white">{progress}%</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-white">
                    {implementation?.phases?.filter(p => p.status === "completed").length || 0}
                  </p>
                  <p className="text-xs text-zinc-500">Phases Done</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {implementation?.phases?.length || 0}
                  </p>
                  <p className="text-xs text-zinc-500">Total Phases</p>
                </div>
              </div>
            </div>

            {/* Key Dates */}
            <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 p-6">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Key Dates</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <PlayCircle className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-zinc-300">Started</span>
                  </div>
                  <span className="text-sm text-white">{formatDate(implementation?.started_at)}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Flag className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-zinc-300">Target Go-Live</span>
                  </div>
                  <input
                    type="date"
                    value={implementation?.target_go_live || ""}
                    onChange={(e) => updateTargetDate(e.target.value)}
                    className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-white"
                    data-testid="target-date"
                  />
                </div>
              </div>
            </div>

            {/* Phase List */}
            <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 p-6">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Phases</h3>
              
              <div className="space-y-2">
                {implementation?.phases?.map((phase) => (
                  <button
                    key={phase.id}
                    onClick={() => setActivePhase(phase.id)}
                    data-testid={`phase-${phase.id}`}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                      activePhase === phase.id
                        ? "bg-purple-500/20 border border-purple-500/50"
                        : "bg-zinc-800/50 border border-transparent hover:border-zinc-700"
                    }`}
                  >
                    {phase.status === "completed" ? (
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                    ) : phase.status === "in_progress" ? (
                      <Clock className="w-5 h-5 text-purple-400" />
                    ) : (
                      <Circle className="w-5 h-5 text-zinc-600" />
                    )}
                    <div className="flex-1 text-left">
                      <p className={`text-sm font-medium ${activePhase === phase.id ? "text-purple-300" : "text-white"}`}>
                        {phase.name}
                      </p>
                      <p className="text-xs text-zinc-500">{getPhaseProgress(phase)}% complete</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 ${activePhase === phase.id ? "text-purple-400" : "text-zinc-600"}`} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Active Phase Details */}
          <div className="col-span-8">
            {currentPhase && (
              <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 p-6">
                {/* Phase Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-xl font-bold text-white">{currentPhase.name}</h2>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        currentPhase.status === "completed"
                          ? "bg-green-500/20 text-green-400"
                          : currentPhase.status === "in_progress"
                          ? "bg-purple-500/20 text-purple-400"
                          : "bg-zinc-700/50 text-zinc-400"
                      }`}>
                        {currentPhase.status === "completed" ? "Completed" :
                         currentPhase.status === "in_progress" ? "In Progress" : "Not Started"}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400">{currentPhase.description}</p>
                  </div>
                  
                  {/* Phase Actions */}
                  <div className="flex items-center gap-2">
                    {currentPhase.status !== "completed" && (
                      <select
                        value={currentPhase.status}
                        onChange={(e) => updatePhaseStatus(currentPhase.id, e.target.value)}
                        className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white"
                      >
                        <option value="not_started">Not Started</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Mark Complete</option>
                      </select>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-zinc-400">Phase Progress</span>
                    <span className="text-sm font-medium text-white">{getPhaseProgress(currentPhase)}%</span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
                      style={{ width: `${getPhaseProgress(currentPhase)}%` }}
                    />
                  </div>
                </div>

                {/* Tasks */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider">Tasks</h3>
                    <Button
                      onClick={() => setShowAddTask(!showAddTask)}
                      variant="ghost"
                      size="sm"
                      className="text-purple-400 hover:text-purple-300"
                      data-testid="add-task-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Task
                    </Button>
                  </div>

                  {/* Add Task Form */}
                  {showAddTask && (
                    <div className="flex items-center gap-3 mb-4 p-4 bg-zinc-800/50 rounded-xl">
                      <input
                        type="text"
                        value={newTaskName}
                        onChange={(e) => setNewTaskName(e.target.value)}
                        placeholder="Task name..."
                        className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                        data-testid="new-task-input"
                      />
                      <select
                        value={newTaskPriority}
                        onChange={(e) => setNewTaskPriority(e.target.value)}
                        className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                      <Button
                        onClick={() => addTask(currentPhase.id)}
                        size="sm"
                        className="bg-purple-500 hover:bg-purple-600"
                        data-testid="save-task-btn"
                      >
                        Add
                      </Button>
                    </div>
                  )}

                  {/* Task List */}
                  <div className="space-y-2">
                    {currentPhase.tasks.map((task) => (
                      <div
                        key={task.id}
                        className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                          task.completed
                            ? "bg-green-500/10 border border-green-500/20"
                            : "bg-zinc-800/50 border border-zinc-700/50 hover:border-zinc-600"
                        }`}
                      >
                        <button
                          onClick={() => toggleTask(currentPhase.id, task.id)}
                          className="flex-shrink-0"
                          data-testid={`task-toggle-${task.id}`}
                        >
                          {task.completed ? (
                            <CheckSquare className="w-5 h-5 text-green-400" />
                          ) : (
                            <Square className="w-5 h-5 text-zinc-500 hover:text-purple-400" />
                          )}
                        </button>
                        
                        <div className="flex-1">
                          <p className={`text-sm ${task.completed ? "text-zinc-400 line-through" : "text-white"}`}>
                            {task.name}
                          </p>
                          {task.completed && task.completed_by && (
                            <p className="text-xs text-zinc-500 mt-1">
                              Completed by {task.completed_by} on {formatDate(task.completed_at)}
                            </p>
                          )}
                        </div>
                        
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          task.priority === "high"
                            ? "bg-red-500/20 text-red-400"
                            : task.priority === "medium"
                            ? "bg-yellow-500/20 text-yellow-400"
                            : "bg-zinc-700/50 text-zinc-400"
                        }`}>
                          {task.priority}
                        </span>
                      </div>
                    ))}
                    
                    {currentPhase.tasks.length === 0 && (
                      <div className="text-center py-8 text-zinc-500">
                        <FileCheck className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>No tasks in this phase yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ZOEY AI Actions - Separate Card */}
            <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800 mt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-gradient-to-r from-yellow-500/20 to-orange-500/20">
                    <Sparkles className="w-4 h-4 text-yellow-400" />
                  </div>
                  <h3 className="font-medium text-white">ZOEY AI Actions</h3>
                </div>
                <span className="text-xs text-zinc-500">Click to open action panel</span>
              </div>
              
              <div className="grid grid-cols-4 gap-3">
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/ai-call`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-emerald-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                    <Phone className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-sm text-zinc-300">AI Call</span>
                </button>
                
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/ai-email`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-blue-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-sm text-zinc-300">AI Email</span>
                </button>
                
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/ai-text`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-purple-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                    <MessageSquare className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-sm text-zinc-300">AI Text</span>
                </button>
                
                <button
                  onClick={() => navigate(`/admin/leads/${leadId}/ai-appointment`)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-orange-500/30 transition-all group"
                >
                  <div className="p-3 rounded-full bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
                    <Calendar className="w-5 h-5 text-orange-400" />
                  </div>
                  <span className="text-sm text-zinc-300">Schedule</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
